import { useEffect, useMemo, useRef, useState } from 'react'
import useSWR from 'swr'
import Select from 'react-select'
import { useDebounce } from '../hooks'
import { useToast } from '../contexts/ToastContext'
import { fetchMvpdProviders, updateMvpdProvider, type MvpdProvider } from '../services/api'
import { Button, OverridableField, Pagination } from '../components'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import selectStyles from '../styles/react-select.module.scss'
import styles from './MvpdProvidersList.module.scss'

const PAGE_SIZE = 50

// Tri-state filter: match all, only-true, or only-false.
type TriState = 'all' | 'yes' | 'no'
type FilterOption = { value: TriState; label: string }

const PROMOTED_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All' },
  { value: 'yes', label: 'Promoted' },
  { value: 'no', label: 'Not promoted' },
]
const ENABLED_OPTIONS: FilterOption[] = [
  { value: 'all', label: 'All' },
  { value: 'yes', label: 'Enabled' },
  { value: 'no', label: 'Not enabled' },
]

// Optimistic overrides for the editable columns, keyed by provider_id.
type Override = Partial<Pick<MvpdProvider, 'enabled' | 'promoted' | 'tc_display_name' | 'display_name'>>

export default function MvpdProvidersList() {
  const { data, isLoading, error, mutate } = useSWR('mvpd-providers', fetchMvpdProviders, {
    revalidateOnFocus: false,
  })
  const { toast } = useToast()

  const [searchTerm, setSearchTerm] = useState('')
  const debouncedSearch = useDebounce(searchTerm, 300)
  const [enabledFilter, setEnabledFilter] = useState<TriState>('all')
  const [promotedFilter, setPromotedFilter] = useState<TriState>('all')
  const [page, setPage] = useState(1)

  const [pending, setPending] = useState<Record<string, Override>>({})
  // Per-provider in-flight save count (ref = synchronous truth); savingIds mirrors
  // it for rendering. nameNonce bumps on each completed save to remount the
  // uncontrolled name input back to server truth.
  const savingCount = useRef<Record<string, number>>({})
  const [savingIds, setSavingIds] = useState<Set<string>>(new Set())
  const [nameNonce, setNameNonce] = useState<Record<string, number>>({})

  // Stable reference so the filter useMemo below doesn't re-run every render.
  const providers = useMemo(() => data?.providers ?? [], [data])
  const enabledCount = providers.filter((p) => p.enabled).length
  const promotedCount = providers.filter((p) => p.promoted).length

  const filtered = useMemo(() => {
    const query = debouncedSearch.trim().toLowerCase()
    return providers.filter((provider) => {
      const override = pending[provider.provider_id]
      const effEnabled = override?.enabled ?? provider.enabled
      const effPromoted = override?.promoted ?? provider.promoted
      if (enabledFilter === 'yes' && !effEnabled) return false
      if (enabledFilter === 'no' && effEnabled) return false
      if (promotedFilter === 'yes' && !effPromoted) return false
      if (promotedFilter === 'no' && effPromoted) return false
      if (!query) return true
      const name = (override?.display_name ?? provider.display_name).toLowerCase()
      return name.includes(query) || provider.provider_id.toLowerCase().includes(query)
    })
  }, [providers, debouncedSearch, enabledFilter, promotedFilter, pending])

  // Any filter change returns to the first page.
  useEffect(() => {
    setPage(1)
  }, [debouncedSearch, enabledFilter, promotedFilter])

  const totalCount = filtered.length
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE))
  const safePage = Math.min(page, totalPages)
  // Clamp back into range when the filtered set shrinks under the current page.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages)
  }, [page, totalPages])

  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE)
  const paginationInfo = {
    page: safePage,
    limit: PAGE_SIZE,
    total_count: totalCount,
    total_pages: totalPages,
    has_next: safePage < totalPages,
    has_prev: safePage > 1,
  }

  const beginSave = (providerId: string) => {
    savingCount.current[providerId] = (savingCount.current[providerId] ?? 0) + 1
    setSavingIds(new Set(Object.keys(savingCount.current)))
  }

  const endSave = (providerId: string) => {
    const remaining = (savingCount.current[providerId] ?? 1) - 1
    if (remaining <= 0) {
      // Last save for this row finished: drop the optimistic override (the cache
      // now holds server truth) and remount the name input to that truth.
      delete savingCount.current[providerId]
      setPending((prev) => {
        const next = { ...prev }
        delete next[providerId]
        return next
      })
      setNameNonce((prev) => ({ ...prev, [providerId]: (prev[providerId] ?? 0) + 1 }))
    } else {
      savingCount.current[providerId] = remaining
    }
    setSavingIds(new Set(Object.keys(savingCount.current)))
  }

  const patchProvider = async (
    providerId: string,
    changes: { enabled?: boolean; promoted?: boolean; tc_display_name?: string | null },
    optimistic: Override
  ) => {
    setPending((prev) => ({ ...prev, [providerId]: { ...prev[providerId], ...optimistic } }))
    beginSave(providerId)
    try {
      const updated = await updateMvpdProvider(providerId, changes)
      // The PATCH response is authoritative — write it straight into the SWR cache
      // (no network revalidation, so a flaky GET can't make a saved change look failed).
      await mutate(
        (current) =>
          current
            ? {
                ...current,
                providers: current.providers.map((p) =>
                  p.provider_id === providerId ? updated : p
                ),
              }
            : current,
        { revalidate: false }
      )
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Failed to update provider', 'error')
    } finally {
      endSave(providerId)
    }
  }

  const toggle = (provider: MvpdProvider, field: 'enabled' | 'promoted') => {
    const next = !(pending[provider.provider_id]?.[field] ?? provider[field])
    void patchProvider(provider.provider_id, { [field]: next }, { [field]: next })
  }

  const commitName = (provider: MvpdProvider, raw: string) => {
    const value = raw.trim()
    // Blank, or the Adobe name typed verbatim, means "no override".
    const target = value === '' || value === provider.ext_display_name ? null : value
    if (target === (provider.tc_display_name ?? null)) return // nothing changed
    void patchProvider(
      provider.provider_id,
      { tc_display_name: target },
      { tc_display_name: target, display_name: target ?? provider.ext_display_name }
    )
  }

  // Undo the display-name override, reverting to Adobe's name.
  const revertName = (provider: MvpdProvider) => {
    void patchProvider(
      provider.provider_id,
      { tc_display_name: null },
      { tc_display_name: null, display_name: provider.ext_display_name }
    )
  }

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Couldn’t load MVPD providers</h2>
          <p>{(error as Error).message}</p>
          <Button variant="secondary" onClick={() => void mutate()}>
            Retry
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.pageHeader}>
        <h1>MVPD Providers</h1>
        <p>Choose which TV providers appear in the sign-in picker and which are featured.</p>
      </div>

      <p className={styles.synced}>
        {data?.last_synced_at
          ? `Adobe list last refreshed ${new Date(data.last_synced_at).toLocaleString()}`
          : 'Adobe list not yet synced'}
        {` · ${providers.length} providers · ${promotedCount} promoted · ${enabledCount} enabled`}
      </p>

      <div className={listTableStyles.searchSection}>
        <div className={listTableStyles.searchForm}>
          <div className={listTableStyles.searchInputGroup}>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search providers by name or id…"
              className={listTableStyles.searchInput}
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm('')} className={listTableStyles.clearButton}>
                Clear
              </button>
            )}
          </div>
          <div className={styles.filterRow}>
            <div className={styles.filterField}>
              <label htmlFor="promotedFilter">Promoted</label>
              <Select<FilterOption>
                inputId="promotedFilter"
                options={PROMOTED_OPTIONS}
                value={PROMOTED_OPTIONS.find((o) => o.value === promotedFilter)}
                onChange={(opt) => setPromotedFilter(opt?.value ?? 'all')}
                isSearchable={false}
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
              />
            </div>
            <div className={styles.filterField}>
              <label htmlFor="enabledFilter">Enabled</label>
              <Select<FilterOption>
                inputId="enabledFilter"
                options={ENABLED_OPTIONS}
                value={ENABLED_OPTIONS.find((o) => o.value === enabledFilter)}
                onChange={(opt) => setEnabledFilter(opt?.value ?? 'all')}
                isSearchable={false}
                className={selectStyles.reactSelect}
                classNamePrefix="react-select"
              />
            </div>
          </div>
        </div>
      </div>

      {isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading…</h3>
        </div>
      ) : filtered.length === 0 ? (
        <div className={listTableStyles.noResults}>
          <h3>No providers match your filters</h3>
        </div>
      ) : (
        <>
          <div className={`${listTableStyles.tableHeader} ${styles.listHeader}`}>
            <div>Logo</div>
            <div>Provider</div>
            <div>Promoted</div>
            <div>Enabled</div>
          </div>
          <div className={listTableStyles.tableBody}>
            {pageItems.map((provider) => {
              const saving = savingIds.has(provider.provider_id)
              const override = pending[provider.provider_id]
              const effEnabled = override?.enabled ?? provider.enabled
              const effPromoted = override?.promoted ?? provider.promoted
              // Effective override (accounting for an in-flight optimistic change):
              // drives the revert button + "Original" line in OverridableField.
              const effTcName =
                override?.tc_display_name !== undefined
                  ? override.tc_display_name
                  : provider.tc_display_name ?? null
              return (
                <div key={provider.provider_id} className={`${listTableStyles.tableRow} ${styles.listItem}`}>
                  <div>
                    {provider.logo_url ? (
                      <img className={styles.logo} src={provider.logo_url} alt="" loading="lazy" />
                    ) : (
                      <span className={styles.logoPlaceholder} aria-hidden="true" />
                    )}
                  </div>
                  <div className={listTableStyles.cellPrimary}>
                    <OverridableField
                      compact
                      tcValue={effTcName}
                      extValue={provider.ext_display_name}
                      onRevert={() => revertName(provider)}
                      modifiedClassName={styles.nameInputModified}
                    >
                      <input
                        key={`${provider.provider_id}|${nameNonce[provider.provider_id] ?? 0}`}
                        className={styles.nameInput}
                        defaultValue={provider.tc_display_name ?? provider.ext_display_name}
                        disabled={saving}
                        aria-label={`Display name for ${provider.provider_id}`}
                        onBlur={(e) => commitName(provider, e.target.value)}
                      />
                    </OverridableField>
                    <p className={listTableStyles.secondaryText}>{provider.provider_id}</p>
                  </div>
                  <div className={listTableStyles.cellText}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={effPromoted}
                      disabled={saving}
                      aria-label={`Promote ${provider.display_name}`}
                      onChange={() => toggle(provider, 'promoted')}
                    />
                  </div>
                  <div className={listTableStyles.cellText}>
                    <input
                      type="checkbox"
                      className={styles.checkbox}
                      checked={effEnabled}
                      disabled={saving}
                      aria-label={`Enable ${provider.display_name}`}
                      onChange={() => toggle(provider, 'enabled')}
                    />
                  </div>
                </div>
              )
            })}
          </div>

          <Pagination pagination={paginationInfo} onPageChange={setPage} itemName="providers" />
        </>
      )}
    </div>
  )
}
